import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, CheckCircle2, AlertCircle, ShieldAlert } from "lucide-react";
import { isNativeApp, prepareTapToPay, getAppleLinkStatus, getTTPCapability } from "../../utils/tapToPay";
import { TapToPayGlyph, ttpLabel } from "../payments/TapToPayButton";
import TapToPayEducation from "../payments/TapToPayEducation";
import TapToPayTryIt from "../payments/TapToPayTryIt";

const API = process.env.REACT_APP_BACKEND_URL;

// Stage 2 — technician device onboarding. Configures THIS technician's iPhone
// through the Stripe Terminal SDK. Technicians never accept Apple's legal terms
// (tos_allowed:false) — the business admin already did that via web onboarding.
export default function TechTapToPaySetup({ isDark }) {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [progress, setProgress] = useState(null);
  const [ready, setReady] = useState(false);
  const [eduOpen, setEduOpen] = useState(false);
  const [tryItOpen, setTryItOpen] = useState(false);
  const [deviceLinked, setDeviceLinked] = useState(null); // null = unknown
  const setupDoneRef = useRef(false);
  const navigate = useNavigate();
  const native = isNativeApp();
  const androidNative = native && getTTPCapability().platform === "android";
  const deviceWord = androidNative ? "phone" : "iPhone";

  useEffect(() => {
    if (!native) return;
    const tok = localStorage.getItem("tech_token");
    if (!tok) return;
    axios.get(`${API}/api/tech/terminal/status`, { headers: { Authorization: `Bearer ${tok}` } })
      .then((r) => setStatus(r.data))
      .catch(() => {});
  }, [native]);

  // Apple req 1.6: when the backend hint says "not linked", double-check with
  // Apple via the Stripe SDK — the SDK status is authoritative on-device.
  // iOS only: Apple merchant linking doesn't exist on Android.
  useEffect(() => {
    if (!native || androidNative || !status?.business_eligible || !status?.has_permission) return;
    if ((status.merchant_onboarding || {}).linked) return;
    getAppleLinkStatus({
      token_endpoint: `${API}/api/tech/terminal/connection-token`,
      auth_token: localStorage.getItem("tech_token"),
      connected_account_id: status.connected_account_id,
    }).then((res) => { if (res.ok) setDeviceLinked(!!res.linked); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [native, status?.business_eligible, status?.has_permission]);

  // Only inside the mobile app, and only when the business has enabled TTP.
  if (!native || !status || !status.business_eligible) return null;

  const onb = status.merchant_onboarding || {};
  // Android Tap to Pay is never gated on Apple's merchant linking.
  const linkedEffective = androidNative || onb.linked || deviceLinked === true;
  const card = `rounded-2xl p-5 space-y-3 ${isDark ? "bg-[#0c0c14] border border-white/5" : "bg-white border border-gray-200"}`;
  const title = (
    <div className="flex items-center justify-between gap-2">
      <h3 className={`text-sm font-bold flex items-center gap-2 ${isDark ? "text-white" : "text-gray-900"}`}>
        <TapToPayGlyph className="w-5 h-5 text-sky-500" /> {ttpLabel()}
      </h3>
      {ready && <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-500"><CheckCircle2 className="w-3.5 h-3.5" /> Ready</span>}
    </div>
  );

  // Not permitted
  if (!status.has_permission) {
    return (
      <div className={card} data-testid="tech-ttp-setup">
        {title}
        <div className="flex items-start gap-2 text-xs text-muted-foreground">
          <ShieldAlert className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
          You don't have the "Collect with Tap to Pay" permission. Ask your administrator to enable it for your account.
        </div>
      </div>
    );
  }

  // Merchant (business admin) hasn't completed Apple linking yet
  if (!linkedEffective) {
    return (
      <div className={card} data-testid="tech-ttp-setup">
        {title}
        <div className="flex items-start gap-2 text-xs text-muted-foreground" data-testid="tech-ttp-admin-blocked">
          <AlertCircle className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
          Your business administrator must activate Tap to Pay first. Once they accept Apple's Terms for your business, you can set up this iPhone here.
        </div>
      </div>
    );
  }

  const runSetup = async () => {
    setBusy(true);
    setMsg("Starting…");
    setProgress(null);
    const res = await prepareTapToPay({
      token_endpoint: `${API}/api/tech/terminal/connection-token`,
      auth_token: localStorage.getItem("tech_token"),
      location_id: status.location_id,
      connected_account_id: status.connected_account_id,
      merchant_display_name: status.merchant_display_name,
      // Technicians can't accept merchant legal terms — the admin already did.
      tos_allowed: false,
    }, { onStatus: (m, _ph, p) => { setMsg(m || ""); setProgress(p ?? null); } });
    setBusy(false);
    setMsg("");
    setProgress(null);
    if (res.ok) {
      setReady(true);
      toast.success(`This ${deviceWord} is ready for Tap to Pay`);
      setupDoneRef.current = true;
      setEduOpen(true);
    } else if (res.error && res.error !== "busy" && res.error !== "timeout") {
      toast.error(res.error);
    }
  };

  return (
    <div className={card} data-testid="tech-ttp-setup">
      {title}
      <p className="text-xs text-muted-foreground">
        Set up this {deviceWord} to accept contactless cards and Apple Pay / Google Pay directly from customers — no card reader or Stripe login needed.
      </p>
      {busy ? (
        <div className="space-y-1.5" data-testid="tech-ttp-progress">
          <p className={`text-xs flex items-center gap-2 ${isDark ? "text-white/90" : "text-gray-900"}`}>
            <Loader2 className="w-3.5 h-3.5 animate-spin" /> {msg || "Preparing…"}
          </p>
          {progress != null && (
            <div className="h-1.5 rounded-full bg-muted overflow-hidden">
              <div className="h-full bg-sky-500 transition-all" style={{ width: `${Math.min(progress, 100)}%` }} />
            </div>
          )}
        </div>
      ) : (
        <button
          onClick={runSetup}
          data-testid="tech-ttp-setup-btn"
          className="w-full flex items-center justify-center gap-2 h-11 rounded-xl bg-sky-600 hover:bg-sky-700 text-white font-semibold text-sm transition-colors"
        >
          <TapToPayGlyph className="w-5 h-5" /> {ready ? `Re-check this ${deviceWord}` : `Set up Tap to Pay on this ${deviceWord}`}
        </button>
      )}
      <TapToPayEducation open={eduOpen} onClose={() => {
        setEduOpen(false);
        // Apple req 3.9: after setup + education, invite the tech to try it.
        if (setupDoneRef.current) { setupDoneRef.current = false; setTryItOpen(true); }
      }} />
      <TapToPayTryIt open={tryItOpen} onClose={() => setTryItOpen(false)}
        onTry={() => navigate("/tech/jobs")} />
    </div>
  );
}
